import i18next from 'i18next';
import moment, {type Moment} from 'moment-timezone';

import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isNonEmpty, map} from 'web-app/util/non-empty-array';

import * as Helpers from '../helpers/helpers';
import {
    getSummaryPricingGroups,
    transformPriceForBiweeklySummaryPeriod,
    transformItemToSummaryPeriodItem,
} from '../transformers/summary-transformers';
import type * as Types from '../types';
import * as DateFormatter from '../helpers/date-formatter';
import {AUTOMATIC_PRICING_GROUP_ID} from '../constants';

const NUMBER_OF_PERIODS_DISPLAYED = 5;

type Args = {
    selectedBillingType: Types.State['selectedBillingType'];
    selectedBillingPeriod: Types.State['billingPeriodState']['selectedBillingPeriod'];
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
    selectedFees: Types.State['feesState']['selectedFees'];
    selectedDiscounts: Types.State['discountState']['selectedDiscounts'];
    selectedEndDate: Types.State['endDateState']['selectedEndDate'];
    selectedStartDate: null | NonNullable<Types.State['customPeriodState']['customPeriodDates']>['startDate'];
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    pricingGroupOptions: Array<Types.PricingGroupOption>;
    selectedBillingFrequency: Types.BillingFrequencyOption | null;
    isEditView: boolean;
    biweeklyBillingPeriodStartDate: Moment;
    showOnePeriod: boolean;
};
type Result = null | {
    summaryPeriods: Array<Types.SummaryPeriod>;
    summaryInfoCopy: string;
    summaryPricingGroups: Types.SummaryPricingGroups;
};

const useSummaryPeriods = ({
    selectedBillingType,
    selectedBillingPeriod,
    selectedTuitions,
    selectedFees,
    selectedDiscounts,
    selectedEndDate,
    selectedStartDate,
    selectedPricingGroup,
    selectedChildren,
    pricingGroupOptions,
    selectedBillingFrequency,
    isEditView,
    biweeklyBillingPeriodStartDate,
    showOnePeriod,
}: Args): Result => {
    if (!selectedBillingType) {
        return null;
    }

    const lastDay = Helpers.getLastDay(selectedChildren);

    const summaryPeriods = getSummaryPeriods(
        selectedBillingPeriod,
        selectedEndDate,
        selectedBillingFrequency,
        isEditView,
        lastDay,
        showOnePeriod,
    );
    if (!hasValue(summaryPeriods) || !isNonEmpty(summaryPeriods.periods)) {
        return null;
    }

    const hasCustomStartPeriod = (index: number) =>
        hasValue(selectedStartDate) &&
        hasValue(selectedBillingFrequency) &&
        !Helpers.isFirstDayOfBillingPeriod(
            selectedStartDate,
            selectedBillingFrequency.type,
            biweeklyBillingPeriodStartDate,
        ) &&
        index === 0;

    const hasCustomEndPeriod = (index: number, lastIndex: number, lastPeriod: Moment) =>
        hasValue(selectedEndDate) &&
        hasValue(selectedBillingFrequency) &&
        isDateBefore(selectedEndDate, lastPeriod) &&
        !Helpers.isEndOfPeriodDate(selectedEndDate, selectedBillingFrequency.type, biweeklyBillingPeriodStartDate) &&
        index === lastIndex;

    const summaryPricingGroupIds: Array<Types.PricingGroup['id']> = getSummaryPricingGroupIds({
        summaryPeriods,
        selectedChildren,
        selectedPricingGroup,
    });
    const uniqueSummaryPricingGroupIds = [...new Set(summaryPricingGroupIds)];
    const summaryPricingGroups = getSummaryPricingGroups(uniqueSummaryPricingGroupIds, pricingGroupOptions);

    // selectedStartDate has a value where customStartDateText but the type system can't derive it.
    const customStartDateText = hasValue(selectedStartDate) ? DateFormatter.textFormat(selectedStartDate) : '';

    const summaryPeriodsWithCustomPeriod = (): null | Array<Types.SummaryPeriod> => {
        switch (summaryPeriods.type) {
            case 'monthly': {
                return summaryPeriods.periods.map((period, index, array) => {
                    const periodPricingGroups = Helpers.initialPeriodPricingGroup(
                        period,
                        selectedPricingGroup,
                        selectedChildren,
                    );

                    if (hasCustomStartPeriod(index)) {
                        return customPeriodSummaryPeriod({
                            type: 'start',
                            title: `${customStartDateText} - ${period.endDate.textFormat}`,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const lastIndex = array.length - 1;
                    const lastPeriod = array[lastIndex];
                    if (hasCustomEndPeriod(index, lastIndex, lastPeriod.unformattedEndDate)) {
                        return customPeriodSummaryPeriod({
                            type: 'end',
                            title: period.month,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const billingInfoByPricingGroup = Helpers.calculateBillingInfoByPricingGroupForStdSummaryPeriod(
                        periodPricingGroups ?? [],
                        period,
                        selectedBillingFrequency,
                        isEditView,
                        selectedTuitions,
                        selectedFees,
                        selectedDiscounts,
                    );

                    const isLastDaySameAsEndOfMonth = period.unformattedEndDate.isSame(
                        period.unformattedEndDate.clone().endOf('month'),
                    );

                    const range = `${period.startDate.textFormat} - ${period.endDate.textFormat}`;
                    const periodTitle = isLastDaySameAsEndOfMonth ? period.month : range;

                    return summaryPeriod({
                        tuitions: selectedTuitions,
                        fees: selectedFees,
                        discounts: selectedDiscounts,
                        selectedPricingGroup,
                        periodPricingGroups: periodPricingGroups ?? [],
                        periodTitle,
                        summaryPricingGroups,
                        billingInfoByPricingGroup,
                    });
                });
            }
            case 'weekly': {
                return summaryPeriods.periods.map((period, index, array) => {
                    const periodPricingGroups = Helpers.initialPeriodPricingGroup(
                        period,
                        selectedPricingGroup,
                        selectedChildren,
                    );

                    if (hasCustomStartPeriod(index)) {
                        return customPeriodSummaryPeriod({
                            type: 'start',
                            title: `${customStartDateText} - ${period.endDate.textFormat}`,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const lastIndex = array.length - 1;
                    const lastPeriod = array[lastIndex];
                    if (hasCustomEndPeriod(index, lastIndex, lastPeriod.unformattedEndDate)) {
                        return customPeriodSummaryPeriod({
                            type: 'end',
                            title: `${period.startDate.textFormat} - ${period.endDate.textFormat}`,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const billingInfoByPricingGroup = Helpers.calculateBillingInfoByPricingGroupForStdSummaryPeriod(
                        periodPricingGroups ?? [],
                        period,
                        selectedBillingFrequency,
                        isEditView,
                        selectedTuitions,
                        selectedFees,
                        selectedDiscounts,
                    );

                    return summaryPeriod({
                        tuitions: selectedTuitions,
                        fees: selectedFees,
                        discounts: selectedDiscounts,
                        selectedPricingGroup,
                        periodPricingGroups: periodPricingGroups ?? [],
                        periodTitle: `${period.startDate.textFormat} - ${period.endDate.textFormat}`,
                        summaryPricingGroups,
                        billingInfoByPricingGroup,
                    });
                });
            }
            case 'biweekly': {
                return summaryPeriods.periods.map((period, index, array) => {
                    const periodPricingGroups = Helpers.initialPeriodPricingGroup(
                        period,
                        selectedPricingGroup,
                        selectedChildren,
                    );

                    if (hasCustomStartPeriod(index)) {
                        return customPeriodSummaryPeriod({
                            type: 'start',
                            title: `${customStartDateText} - ${period.endDate.textFormat}`,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const lastIndex = array.length - 1;
                    const lastPeriod = array[lastIndex];
                    if (hasCustomEndPeriod(index, lastIndex, lastPeriod.unformattedEndDate)) {
                        return customPeriodSummaryPeriod({
                            type: 'end',
                            title: `${period.startDate.textFormat} - ${period.endDate.textFormat}`,
                            tuitions: selectedTuitions,
                            fees: selectedFees,
                            discounts: selectedDiscounts,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                            isEditView,
                        });
                    }

                    const billingInfoByPricingGroup = Helpers.calculateBillingInfoByPricingGroupForStdSummaryPeriod(
                        periodPricingGroups ?? [],
                        period,
                        selectedBillingFrequency,
                        isEditView,
                        selectedTuitions,
                        selectedFees,
                        selectedDiscounts,
                    );

                    return summaryPeriod({
                        tuitions: transformPriceForBiweeklySummaryPeriod(selectedTuitions),
                        fees: transformPriceForBiweeklySummaryPeriod(selectedFees),
                        discounts: selectedDiscounts,
                        selectedPricingGroup,
                        periodPricingGroups: periodPricingGroups ?? [],
                        periodTitle: `${period.startDate.textFormat} - ${period.endDate.textFormat}`,
                        summaryPricingGroups,
                        billingInfoByPricingGroup,
                    });
                });
            }
            default:
                return null;
        }
    };

    const _summaryPeriodsWithCustomPeriod = summaryPeriodsWithCustomPeriod();

    if (!_summaryPeriodsWithCustomPeriod) {
        return null;
    }

    return {
        summaryPeriods: _summaryPeriodsWithCustomPeriod,
        summaryInfoCopy: summaryInfoCopy(selectedEndDate, summaryPeriods, lastDay, showOnePeriod),
        summaryPricingGroups,
    };
};

const customPeriodSummaryPeriod = ({
    type,
    title,
    tuitions,
    fees,
    discounts,
    selectedPricingGroup,
    periodPricingGroups,
    summaryPricingGroups,
    isEditView,
}: {
    type: 'start' | 'end';
    title: string;
    tuitions: Types.State['tuitionsState']['selectedTuitions'];
    fees: Types.State['feesState']['selectedFees'];
    discounts: Types.State['discountState']['selectedDiscounts'];
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    periodPricingGroups: null | Array<Types.PeriodPricingGroup>;
    summaryPricingGroups: Types.SummaryPricingGroups;
    isEditView: boolean;
}): Types.SummaryPeriod => {
    const summaryPeriodItem = (
        tuition: typeof tuitions[number] | typeof fees[number],
        customPeriod: typeof tuition['customStartPeriod'] | typeof tuition['customEndPeriod'],
    ): Types.SummaryPeriodItem => {
        if (hasValue(customPeriod)) {
            return transformItemToSummaryPeriodItem({
                item: {
                    ...tuition,
                    pricingGroups: map(tuition.pricingGroups, pricingGroup => {
                        return {
                            id: pricingGroup.id,
                            price: customPeriod.price,
                        };
                    }),
                },
                selectedPricingGroup,
                periodPricingGroups,
                summaryPricingGroups,
            });
        }
        return transformItemToSummaryPeriodItem({
            item: tuition,
            selectedPricingGroup,
            periodPricingGroups,
            summaryPricingGroups,
        });
    };
    const tuitionSummaryPeriodItems = tuitions.map<Types.SummaryPeriodItem>(tuition => {
        if (type === 'start') {
            return summaryPeriodItem(tuition, tuition.customStartPeriod);
        }
        return summaryPeriodItem(tuition, tuition.customEndPeriod);
    });
    const feeSummaryPeriodItems = fees.map<Types.SummaryPeriodItem>(fee => {
        if (type === 'start') {
            return summaryPeriodItem(fee, fee.customStartPeriod);
        }
        return summaryPeriodItem(fee, fee.customEndPeriod);
    });

    const summaryPeriodDiscounts = isEditView
        ? (() => {
              const selectedTuitionAndFeePricingGroups = [
                  ...tuitionSummaryPeriodItems.flatMap(summaryPeriodItem => summaryPeriodItem.pricingGroups),
                  ...feeSummaryPeriodItems.flatMap(summaryPeriodItem => summaryPeriodItem.pricingGroups),
              ];
              const summaryPeriodTotals = Helpers.sortPricingGroups(
                  Helpers.calculatePricingGroupTotals(selectedTuitionAndFeePricingGroups),
                  summaryPricingGroups,
              );
              const discountAmountsAndTotal = Helpers.calculateDiscountAmountsAndTotal(
                  Helpers.sortDiscounts(discounts),
                  summaryPeriodTotals,
              );
              return isNonEmpty(discounts)
                  ? discountAmountsAndTotal.discounts.map(discount =>
                        transformItemToSummaryPeriodItem({
                            item: discount,
                            selectedPricingGroup,
                            periodPricingGroups,
                            summaryPricingGroups,
                        }),
                    )
                  : [];
          })()
        : [];

    // Custom periods are always unknown in edit mode
    const billingInfo: Types.SummaryBillingInfo = isEditView
        ? {
              isAlreadyBilled: false,
              priceQuality: 'unknown',
              priceQualityReason: 'customPeriod',
          }
        : {
              isAlreadyBilled: false,
              priceQuality: 'planned',
          };

    return {
        title,
        tuitions: tuitionSummaryPeriodItems,
        fees: feeSummaryPeriodItems,
        discounts: summaryPeriodDiscounts,
        totalsByPricingGroup: Helpers.sortPricingGroups(
            Helpers.calculatePricingGroupTotals([
                ...tuitionSummaryPeriodItems.flatMap(({pricingGroups}) => pricingGroups),
                ...feeSummaryPeriodItems.flatMap(({pricingGroups}) => pricingGroups),
            ]),
            summaryPricingGroups,
        ),
        hasChildThatMovesPricingGroups: isOneChildPresentInMultiplePricingGroups(periodPricingGroups),
        billingInfoByPricingGroup: tuitionSummaryPeriodItems.map(() => billingInfo),
        tooltip: isEditView
            ? undefined
            : {
                  title: i18next.t('react.simplifiedUsBillingPlan.translations.json.chargedAsPurchaseTooltip.title'),
                  content: i18next.t(
                      'react.simplifiedUsBillingPlan.translations.json.chargedAsPurchaseTooltip.content',
                  ),
              },
    };
};

type SummaryPeriods = (
    selectedBillingPeriod: Types.Month | Types.Week | Types.CustomPeriod | null,
    selectedEndDate: Types.EndDate | null,
    selectedBillingFrequency: Types.BillingFrequencyOption | null,
    isEditView: boolean,
    lastDay: Moment | null,
    showOnePeriod: boolean,
) => null | Types.SummaryPeriods;
const getSummaryPeriods: SummaryPeriods = (
    selectedBillingPeriod,
    selectedEndDate,
    selectedBillingFrequency,
    isEditView: boolean,
    lastDay,
    showOnePeriod,
) => {
    const unformattedStartDate = selectedBillingPeriod?.unformattedStartDate;
    const unformattedEndDate = selectedBillingPeriod?.unformattedEndDate;

    if (!hasValue(unformattedStartDate)) {
        return null;
    }

    const calculatedNumberOfSummaryPeriods: number = (() => {
        if (showOnePeriod) {
            return 1;
        }
        if (lastDay) {
            return calculateNumberOfSummaryPeriodsToLastDay(
                selectedEndDate,
                unformattedStartDate,
                selectedBillingFrequency,
                lastDay,
            );
        }

        return calculateNumberOfSummaryPeriodsToEndDate(
            selectedEndDate,
            unformattedStartDate,
            selectedBillingFrequency,
            isEditView,
        );
    })();

    // This guarantees that even if calculatedNumberOfSummaryPeriods is 0 or negative, it won't break.
    const numberOfSummaryPeriods = [...Array(Math.max(0, calculatedNumberOfSummaryPeriods)).keys()];

    switch (selectedBillingFrequency?.type) {
        case 'monthly': {
            return monthlySummaryPeriods(unformattedStartDate, numberOfSummaryPeriods, lastDay);
        }
        case 'weekly': {
            return weeklySummaryPeriods(unformattedStartDate, numberOfSummaryPeriods, lastDay);
        }
        case 'biweekly': {
            if (!hasValue(unformattedEndDate)) {
                return null;
            }
            return isEditView
                ? biweeklySummaryPeriodsFrom(unformattedStartDate, unformattedEndDate, numberOfSummaryPeriods, lastDay)
                : biweeklySummaryPeriods(unformattedStartDate, unformattedEndDate, numberOfSummaryPeriods, lastDay);
        }
        default:
            return null;
    }
};

export const monthlySummaryPeriods = (
    selectedBillingPeriodStartDate: Moment,
    numberOfSummaryPeriods: Array<number>,
    lastDay: Moment | null,
): Types.MonthlySummaryPeriods => {
    const periods = numberOfSummaryPeriods.map<Types.Month>(numberOfSummaryPeriod => {
        const month = selectedBillingPeriodStartDate.clone().add(numberOfSummaryPeriod, 'month').format('MMMM');
        const unformattedStartDate = selectedBillingPeriodStartDate
            .clone()
            .add(numberOfSummaryPeriod, 'month')
            .startOf('month');
        const unformattedEndDate = selectedBillingPeriodStartDate
            .clone()
            .add(numberOfSummaryPeriod, 'month')
            .endOf('month');

        const year = selectedBillingPeriodStartDate
            .clone()
            .add(numberOfSummaryPeriod, 'month')
            .startOf('month')
            .format('YYYY');

        if (lastDay && unformattedEndDate.isAfter(lastDay) && unformattedStartDate.isBefore(lastDay)) {
            return createSummaryPeriod(unformattedStartDate, lastDay, month, year);
        }

        return createSummaryPeriod(unformattedStartDate, unformattedEndDate, month, year);
    });

    return {type: 'monthly', periods};
};

export const weeklySummaryPeriods = (
    selectedBillingPeriodStartDate: Moment,
    numberOfSummaryPeriods: Array<number>,
    lastDay: Moment | null,
): Types.WeeklySummaryPeriods => {
    const periods = numberOfSummaryPeriods.map<Types.Week>(numberOfSummaryPeriod => {
        const unformattedStartDate =
            numberOfSummaryPeriod === 0
                ? selectedBillingPeriodStartDate
                : selectedBillingPeriodStartDate.clone().startOf('isoWeek').add(numberOfSummaryPeriod, 'week');
        const unformattedEndDate = selectedBillingPeriodStartDate
            .clone()
            .startOf('isoWeek')
            .add(numberOfSummaryPeriod, 'week')
            .endOf('isoWeek');
        const year = selectedBillingPeriodStartDate
            .clone()
            .startOf('isoWeek')
            .add(numberOfSummaryPeriod, 'week')
            .format('YYYY');

        if (lastDay && unformattedEndDate.isAfter(lastDay) && unformattedStartDate.isBefore(lastDay)) {
            return createSummaryPeriod(unformattedStartDate, lastDay, undefined, year);
        }

        return createSummaryPeriod(unformattedStartDate, unformattedEndDate, undefined, year);
    });
    return {type: 'weekly', periods};
};

export const biweeklySummaryPeriods = (
    selectedBillingPeriodStartDate: Moment,
    selectedBillingPeriodEndDate: Moment,
    numberOfSummaryPeriods: Array<number>,
    lastDay: Moment | null,
): Types.BiweeklySummaryPeriods => {
    const periods = numberOfSummaryPeriods.map<Types.TwoWeeks>(numberOfSummaryPeriod => {
        const unformattedEndDate = selectedBillingPeriodEndDate.clone().add(numberOfSummaryPeriod * 2, 'weeks');

        const unformattedStartDate =
            numberOfSummaryPeriod === 0
                ? selectedBillingPeriodStartDate
                : unformattedEndDate.clone().subtract(13, 'days');

        if (lastDay && unformattedEndDate.isAfter(lastDay) && unformattedStartDate.isBefore(lastDay)) {
            return createSummaryPeriod(unformattedStartDate, lastDay);
        }

        return createSummaryPeriod(unformattedStartDate, unformattedEndDate);
    });

    return {type: 'biweekly', periods};
};

const biweeklySummaryPeriodsFrom = (
    periodAlignedStartDate: Moment,
    endDate: Moment,
    numberOfSummaryPeriods: Array<number>,
    lastDay: Moment | null,
): Types.BiweeklySummaryPeriods => {
    const periods = numberOfSummaryPeriods.map((numberOfSummaryPeriod): Types.TwoWeeks => {
        const periodStartDate = periodAlignedStartDate.clone().add(2 * numberOfSummaryPeriod, 'weeks');
        const periodEndDateInclusive = periodStartDate.clone().add(2, 'weeks').subtract(1, 'day');
        const endDateInclusive = endDate.isBefore(periodEndDateInclusive) ? endDate : periodEndDateInclusive;

        if (lastDay && endDateInclusive.isAfter(lastDay) && periodStartDate.isBefore(lastDay)) {
            return createSummaryPeriod(periodStartDate, lastDay);
        }

        return createSummaryPeriod(periodStartDate, endDateInclusive);
    });

    return {type: 'biweekly', periods: periods.filter(hasValue)};
};

const createSummaryPeriod = (startDate: Moment, lastDay: Moment, month?: string, year?: string): Types.Month => {
    return {
        month: month ?? '',
        startDate: {
            numericFormat: DateFormatter.numericFormat(startDate),
            textFormat: DateFormatter.textFormat(startDate),
        },
        endDate: {
            numericFormat: DateFormatter.numericFormat(lastDay),
            textFormat: DateFormatter.textFormat(lastDay),
        },
        year: year ?? startDate.format('YYYY'),
        unformattedStartDate: startDate,
        unformattedEndDate: lastDay,
        type: 'generic',
    };
};

const summaryInfoCopy = (
    endDate: Moment | null,
    summaryPeriods: null | Types.SummaryPeriods,
    lastDay: Moment | null,
    showOnePeriod: boolean,
) => {
    if (showOnePeriod) {
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.summaryInfos.planContinuesUntilNext');
    }

    if (hasValue(lastDay)) {
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.summaryInfos.noMoreInvoices');
    }

    if (!hasValue(endDate) || !hasValue(summaryPeriods)) {
        return i18next.t('react.simplifiedUsBillingPlan.translations.json.summaryInfo');
    }

    const lastPeriod = isNonEmpty(summaryPeriods.periods)
        ? summaryPeriods.periods[summaryPeriods.periods.length - 1]
        : null;
    const lastPeriodEndDate = hasValue(lastPeriod)
        ? moment(`${lastPeriod.endDate.numericFormat}/${lastPeriod?.year}`, 'M/D/YYYY')
        : null;

    if (hasValue(lastPeriodEndDate) && lastPeriodEndDate.isBefore(endDate)) {
        return `${needsTranslations('SimplifiedUsBilling', 'Billing will proceed until')} ${endDate.format(
            'M/D/YYYY',
        )}`;
    }

    return i18next.t('react.simplifiedUsBillingPlan.translations.json.summaryInfos.noMoreInvoices');
};

const calculateNumberOfSummaryPeriodsToEndDate = (
    selectedEndDate: Types.EndDate | null,
    selectedBillingPeriodStartDate:
        | Types.Month['unformattedStartDate']
        | Types.Week['unformattedStartDate']
        | undefined,
    selectedBillingFrequency: Types.BillingFrequencyOption | null,
    isEditView: boolean,
): number => {
    if (!hasValue(selectedBillingPeriodStartDate) || !hasValue(selectedBillingFrequency)) {
        return 0;
    }

    if (!hasValue(selectedEndDate)) {
        return NUMBER_OF_PERIODS_DISPLAYED;
    }

    const numberOfPeriods = calculateNumberOfSummaryPeriodsBetween(
        selectedBillingPeriodStartDate,
        selectedEndDate,
        selectedBillingFrequency.type,
    );

    if (!hasValue(numberOfPeriods)) {
        return 0;
    }

    if (!isEditView && numberOfPeriods >= NUMBER_OF_PERIODS_DISPLAYED) {
        return NUMBER_OF_PERIODS_DISPLAYED;
    }

    return numberOfPeriods + 1;
};

const calculateNumberOfSummaryPeriodsToLastDay = (
    selectedEndDate: Types.EndDate | null,
    selectedBillingPeriodStartDate:
        | Types.Month['unformattedStartDate']
        | Types.Week['unformattedStartDate']
        | undefined,
    selectedBillingFrequency: Types.BillingFrequencyOption | null,
    lastDay: Moment,
): number => {
    if (
        !hasValue(selectedBillingPeriodStartDate) ||
        !hasValue(selectedBillingFrequency) ||
        lastDay.isBefore(selectedBillingPeriodStartDate)
    ) {
        return 0;
    }

    const endDate = selectedEndDate ?? lastDay;

    const isLastDayBeforeEndDate = lastDay.isBefore(selectedEndDate);

    const numberOfPeriods = calculateNumberOfSummaryPeriodsBetween(
        selectedBillingPeriodStartDate,
        isLastDayBeforeEndDate ? lastDay : endDate,
        selectedBillingFrequency.type,
    );

    if (!hasValue(numberOfPeriods)) {
        return 0;
    }

    return numberOfPeriods + 1;
};

const calculateNumberOfSummaryPeriodsBetween = (
    startDate: Moment,
    endDate: Moment,
    billingFrequency: Types.BillingFrequencyType,
): number | null => {
    switch (billingFrequency) {
        case 'monthly':
            return endDate.diff(startDate, 'months');
        case 'weekly':
            return endDate.diff(startDate, 'weeks');
        case 'biweekly':
            return Math.floor(endDate.diff(startDate, 'days') / 14);
        default:
            return null;
    }
};

export const isOneChildPresentInMultiplePricingGroups = (
    periodPricingGroups: null | Array<Types.PeriodPricingGroup>,
): boolean => {
    if (!periodPricingGroups) {
        return false;
    }

    const childrenForAllPricingGroups = Object.values(periodPricingGroups).flatMap(({children}) => children);
    const uniqueChildrenForAllPricingGroups = new Set(childrenForAllPricingGroups);

    return childrenForAllPricingGroups.length !== uniqueChildrenForAllPricingGroups.size;
};

const summaryPeriod = ({
    tuitions,
    fees,
    discounts,
    selectedPricingGroup,
    periodPricingGroups,
    summaryPricingGroups,
    periodTitle,
    billingInfoByPricingGroup,
}: {
    tuitions: Array<Types.Tuition>;
    fees: Array<Types.Fee>;
    discounts: Array<Types.Discount>;
    selectedPricingGroup: null | Types.PricingGroup['id'];
    periodPricingGroups: Array<Types.PeriodPricingGroup>;
    summaryPricingGroups: Types.SummaryPricingGroups;
    periodTitle: string;
    billingInfoByPricingGroup: Types.SummaryBillingInfo[];
}): Types.SummaryPeriod => {
    const summaryPeriodTuitions = tuitions.map(tuition => {
        const tuitionSummaryPeriodItem = transformItemToSummaryPeriodItem({
            item: tuition,
            selectedPricingGroup,
            periodPricingGroups,
            summaryPricingGroups,
        });

        return {
            ...tuitionSummaryPeriodItem,
            pricingGroups: tuitionSummaryPeriodItem.pricingGroups.map(pricingGroup => ({
                ...pricingGroup,
                price: tuition.overriddenPrice ?? pricingGroup.price,
            })),
        };
    });
    const summaryPeriodFees = fees
        .map(fee =>
            transformItemToSummaryPeriodItem({
                item: fee,
                selectedPricingGroup,
                periodPricingGroups,
                summaryPricingGroups,
            }),
        )
        .filter(hasValue);
    const selectedTuitionAndFeePricingGroups = [
        ...summaryPeriodTuitions.flatMap(summaryPeriodItem => summaryPeriodItem.pricingGroups),
        ...summaryPeriodFees.flatMap(summaryPeriodItem => summaryPeriodItem.pricingGroups),
    ];

    const summaryPeriodTotals = Helpers.sortPricingGroups(
        Helpers.calculatePricingGroupTotals(selectedTuitionAndFeePricingGroups),
        summaryPricingGroups,
    );

    const discountAmountsAndTotal = Helpers.calculateDiscountAmountsAndTotal(
        Helpers.sortDiscounts(discounts),
        summaryPeriodTotals,
    );

    const summaryPeriodTotalWithDiscounts = isNonEmpty(discounts)
        ? discountAmountsAndTotal.totals
        : summaryPeriodTotals;

    const summaryPeriodTotalWithDiscountsCappedToZero = summaryPeriodTotalWithDiscounts.map(total => ({
        ...total,
        price: total.price < 0 ? 0 : total.price,
    }));

    const summaryPeriodDiscounts = isNonEmpty(discounts)
        ? discountAmountsAndTotal.discounts.map(discount =>
              transformItemToSummaryPeriodItem({
                  item: discount,
                  selectedPricingGroup,
                  periodPricingGroups,
                  summaryPricingGroups,
              }),
          )
        : [];

    return {
        title: periodTitle,
        tuitions: summaryPeriodTuitions,
        fees: summaryPeriodFees,
        discounts: summaryPeriodDiscounts,
        totalsByPricingGroup: summaryPeriodTotalWithDiscountsCappedToZero,
        hasChildThatMovesPricingGroups: isOneChildPresentInMultiplePricingGroups(periodPricingGroups),
        billingInfoByPricingGroup,
    };
};

export const getSummaryPricingGroupIds = ({
    summaryPeriods,
    selectedChildren,
    selectedPricingGroup,
}: {
    summaryPeriods: Types.SummaryPeriods;
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
}): Array<Types.PricingGroup['id']> => {
    return summaryPeriods.periods.flatMap(period => {
        // If only one child is selected, we must show only one column with an empty header (hence the empty string) instead of pricing groups
        if (selectedChildren.length === 1) {
            return '';
        }

        // If a specific pricing group is selected (but not Automatic), we must only show one column with an empty header
        if (selectedPricingGroup && selectedPricingGroup !== AUTOMATIC_PRICING_GROUP_ID) {
            return '';
        }

        const periodPricingGroups = Helpers.periodPricingGroups({periodDateRange: period, selectedChildren});
        return periodPricingGroups.map(periodPricingGroup => periodPricingGroup.pricingGroupId);
    });
};

/**
 * Compares two dates if `former` is a strictly earlier date than `latter`.
 *
 * Ignores the time part.
 */
const isDateBefore = (former: Moment, latter: Moment): boolean => stripTime(former).isBefore(stripTime(latter));

/** Strips the time part of a date. */
const stripTime = (date: Moment): Moment => date.clone().startOf('day');

export {useSummaryPeriods, summaryPeriod};
